import React, { useCallback, useState } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import clsx from 'clsx';
import { Conversation } from 'sb-lib/types';
import log from 'shared/utils/log';
import { useAppDispatch, useAppSelector } from 'shared/store';
import {
  removeConversation,
  resetConversation,
  selectNextOpenConversation,
  toggleConversation,
} from 'shared/store/conversation-slice';
import { message } from 'antd';
import { Button } from 'shared/components/Button';
import { Icon } from 'shared/components/Icon';
import './style.less';

interface SessionChatOpenCloseConversationProps {
  conversation: Conversation | null;
  openConversation?: (visitorId: string, conversationId: number) => void;
}

export const SessionChatOpenCloseConversation: React.FC<SessionChatOpenCloseConversationProps> = ({
  conversation,
  openConversation,
}) => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const isOpen = !!conversation?.isOpen;
  const nextConversation = useAppSelector(s =>
    conversation ? selectNextOpenConversation(s, conversation.id) : undefined,
  );

  const handleToggle = useCallback(async () => {
    if (!conversation) return;
    setIsLoading(true);
    try {
      const res = await dispatch(
        toggleConversation({ isOpen, conversationId: conversation.id }),
      );
      if (isOpen && nextConversation && openConversation) {
        openConversation(nextConversation.visitor.id, nextConversation.id);
      }
      unwrapResult(res);
      dispatch(removeConversation(conversation.id));
      dispatch(resetConversation());
    } catch (err) {
      log.error(`Failed to ${isOpen ? 'close' : 'open'} conversation`, err);
      message.error(err.message || `Failed to ${isOpen ? 'close' : 'open'} conversation`);
    }
    setIsLoading(false);
  }, [conversation, dispatch, isOpen, nextConversation, openConversation]);

  if (!conversation) return null;

  return (
    <Button
      type="ghost"
      className={clsx(
        'SessionChatOpenCloseConversation',
        `SessionChatOpenCloseConversation--${isOpen ? 'close' : 'open'}`,
      )}
      icon={isOpen ? <Icon icon="checkmark" /> : null}
      loading={isLoading}
      onClick={handleToggle}
    >
      {isOpen ? 'Close' : 'Open'}
    </Button>
  );
};
